import { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { DashboardLayout } from "@/components/layout/DashboardLayout";
import api from "@/lib/api";
import { toast } from "sonner";
import { fmtDate } from "@/lib/credits";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Search, Loader2, Download, ChevronLeft, ChevronRight, Check, Ban } from "lucide-react";

function StatusBadge({ status }) {
  const map = {
    active: "bg-emerald-500/10 text-emerald-400 border-emerald-500/20",
    pending_approval: "bg-amber-500/10 text-amber-400 border-amber-500/20",
    suspended: "bg-[#E30613]/10 text-[#FF6069] border-[#E30613]/20",
  };
  const label = { active: "Active", pending_approval: "Pending", suspended: "Suspended" }[status] || status;
  return <span className={`inline-flex rounded-full border px-2.5 py-0.5 text-xs font-medium ${map[status] || "bg-white/5 text-neutral-400 border-white/10"}`}>{label}</span>;
}

export default function Installers() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [data, setData] = useState({ rows: [], total: 0, page: 1, page_size: 20 });
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState(searchParams.get("status") || "all");
  const [sort, setSort] = useState("created_at");
  const [page, setPage] = useState(1);
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, page_size: 20, sort, order: sort === "business_name" ? 1 : -1 };
      if (status !== "all") params.status = status;
      if (q.trim()) params.q = q.trim();
      const { data } = await api.get("/admin/directory", { params });
      setData(data);
    } catch (e) {} finally { setLoading(false); }
  }, [page, sort, status, q]);

  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); }, [load]);
  useEffect(() => { setPage(1); }, [q, status, sort]);

  const act = async (e, id, action) => {
    e.stopPropagation();
    setBusy(id + action);
    try {
      await api.post(`/installers/${id}/${action}`);
      toast.success(action === "approve" ? "Installer approved" : "Installer suspended");
      load();
    } catch (e) { toast.error("Action failed"); } finally { setBusy(null); }
  };

  const exportCsv = async () => {
    try {
      const res = await api.get("/admin/export/installers", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = "installers.csv"; a.click();
      URL.revokeObjectURL(url);
      toast.success("Exported installers");
    } catch (e) { toast.error("Export failed"); }
  };

  const totalPages = Math.max(1, Math.ceil(data.total / data.page_size));

  return (
    <DashboardLayout title="Installers">
      <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-[#E30613]">Directory</p>
          <h2 className="mt-2 font-display text-2xl font-semibold tracking-tight text-white">Installer directory</h2>
          <p className="mt-1 text-sm text-neutral-500">{data.total} installers · manage every account.</p>
        </div>
        <button onClick={exportCsv} className="inline-flex items-center gap-2 self-start rounded-lg border border-white/[0.1] px-4 py-2.5 text-sm font-medium text-neutral-200 transition-colors hover:bg-white/[0.05]" data-testid="export-installers-btn"><Download className="h-4 w-4" /> Export CSV</button>
      </div>

      <div className="mb-5 flex flex-col gap-3 sm:flex-row">
        <div className="relative max-w-md flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-500" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search business, owner, email, phone, city…" className="h-11 rounded-lg border-white/[0.1] bg-[#0A0A0A] pl-10 text-white placeholder:text-neutral-600 focus-visible:border-[#E30613] focus-visible:ring-1 focus-visible:ring-[#E30613]" data-testid="installer-search" />
        </div>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="h-11 w-full rounded-lg border-white/[0.1] bg-[#0A0A0A] text-white sm:w-44" data-testid="installer-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent className="border-white/[0.08] bg-[#141414] text-white">
            <SelectItem value="all">All Statuses</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="pending_approval">Pending</SelectItem>
            <SelectItem value="suspended">Suspended</SelectItem>
          </SelectContent>
        </Select>
        <Select value={sort} onValueChange={setSort}>
          <SelectTrigger className="h-11 w-full rounded-lg border-white/[0.1] bg-[#0A0A0A] text-white sm:w-48" data-testid="installer-sort"><SelectValue /></SelectTrigger>
          <SelectContent className="border-white/[0.08] bg-[#141414] text-white">
            <SelectItem value="created_at">Newest</SelectItem>
            <SelectItem value="business_name">Business Name</SelectItem>
            <SelectItem value="certificate_credits">Available Credits</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="overflow-hidden rounded-2xl border border-white/[0.06] bg-[#141414]" data-testid="installers-table">
        {loading ? (
          <div className="flex items-center justify-center py-20 text-neutral-500"><Loader2 className="h-5 w-5 animate-spin" /></div>
        ) : data.rows.length === 0 ? (
          <div className="py-20 text-center text-sm text-neutral-500">No installers found.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="border-b border-white/[0.06] text-xs uppercase tracking-wider text-neutral-500">
                  <th className="px-6 py-4 font-medium">Business</th>
                  <th className="px-6 py-4 font-medium">Contact</th>
                  <th className="px-6 py-4 font-medium">Status</th>
                  <th className="px-6 py-4 font-medium">Credits</th>
                  <th className="px-6 py-4 font-medium">Certs</th>
                  <th className="px-6 py-4 font-medium">Activations</th>
                  <th className="px-6 py-4 font-medium">Joined</th>
                  <th className="px-6 py-4 text-right font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {data.rows.map((r) => (
                  <tr key={r.id} onClick={() => navigate(`/installers/${r.id}`)} className="cursor-pointer border-b border-white/[0.04] transition-colors hover:bg-white/[0.03]" data-testid={`installer-row-${r.id}`}>
                    <td className="px-6 py-4"><p className="font-medium text-white">{r.business_name}</p><p className="text-xs text-neutral-500">{r.email}</p></td>
                    <td className="px-6 py-4"><p className="text-sm text-neutral-300">{r.contact_name}</p><p className="text-xs text-neutral-500">{r.phone}</p></td>
                    <td className="px-6 py-4"><StatusBadge status={r.status} /></td>
                    <td className="px-6 py-4 text-sm font-medium text-white">{r.available_credits}</td>
                    <td className="px-6 py-4 text-sm text-neutral-300">{r.certificates_generated}</td>
                    <td className="px-6 py-4 text-sm text-neutral-300">{r.bottle_activations}</td>
                    <td className="px-6 py-4 text-sm text-neutral-400">{fmtDate(r.created_at)}</td>
                    <td className="px-6 py-4" onClick={(e) => e.stopPropagation()}>
                      <div className="flex justify-end gap-2">
                        {r.status !== "active" && <button onClick={(e) => act(e, r.id, "approve")} disabled={busy === r.id + "approve"} className="inline-flex items-center gap-1.5 rounded-lg bg-[#E30613] px-3 py-1.5 text-xs font-medium text-white hover:bg-[#FF1C2A] disabled:opacity-60" data-testid={`approve-${r.id}`}>{busy === r.id + "approve" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />} Approve</button>}
                        {r.status === "active" && <button onClick={(e) => act(e, r.id, "suspend")} disabled={busy === r.id + "suspend"} className="inline-flex items-center gap-1.5 rounded-lg border border-white/[0.1] px-3 py-1.5 text-xs font-medium text-neutral-300 hover:bg-white/[0.05] disabled:opacity-60" data-testid={`suspend-${r.id}`}>{busy === r.id + "suspend" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Ban className="h-3.5 w-3.5" />} Suspend</button>}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="mt-4 flex items-center justify-between">
        <p className="text-sm text-neutral-500">Page {data.page} of {totalPages}</p>
        <div className="flex gap-2">
          <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1} className="inline-flex items-center gap-1 rounded-lg border border-white/[0.1] px-3 py-2 text-sm text-neutral-300 hover:bg-white/[0.05] disabled:opacity-40" data-testid="prev-page"><ChevronLeft className="h-4 w-4" /> Prev</button>
          <button onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page >= totalPages} className="inline-flex items-center gap-1 rounded-lg border border-white/[0.1] px-3 py-2 text-sm text-neutral-300 hover:bg-white/[0.05] disabled:opacity-40" data-testid="next-page">Next <ChevronRight className="h-4 w-4" /></button>
        </div>
      </div>
    </DashboardLayout>
  );
}
